/**
 * Typed positional SQL parameters for the query APIs (EHQ-115).
 *
 * A step passes SQL containing `$1`-style placeholders plus an out-of-band array
 * of values. The values travel to newton as a JSON array and bind for real —
 * driver-level for external databases, DataFusion `ParamValues` for datasets —
 * instead of being substituted into the SQL text.
 *
 * ## Twin file
 *
 * ehq-enoch carries the other half of this hand-maintained pair at
 * `root/src/services/query-params.ts`. The two must stay identical: they define
 * the client half of a wire contract, and the execution-parity requirement means
 * a step must behave the same in the browser and headless. Change both together.
 *
 * ## Why validate here instead of letting newton reject
 *
 * newton's `TypedValue` is an untagged serde enum over the JSON scalars, so a
 * nested array or object fails at deserialization with a message that names
 * serde internals rather than the step author's mistake. Checking here turns
 * that into an error naming the method, the offending index, and what was
 * expected — while enforcing exactly the same rule.
 */

/** A single positional SQL parameter. Mirrors newton's `TypedValue`. */
export type TypedParam = string | number | boolean | null;

/**
 * True when step code used the options-object form rather than positional
 * arguments.
 *
 * Deliberately narrow: a plain object only. Strings are the positional form,
 * and an array would be a params list passed in the wrong position — both must
 * fall through so the caller's own positional handling reports it.
 *
 * Expects an already-unwrapped value; see [`unwrapDict`].
 */
export function isQueryOptions(value: unknown): value is Record<string, unknown> {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        !(value instanceof Date)
    );
}

/**
 * Convert a Pyodide `PyProxy` wrapping a Python dict into a plain object.
 *
 * Bracket access on a `PyProxy` does not read the dict's items, so an options
 * object arriving from Python has to be converted before its fields can be read
 * — `o['query']` on the raw proxy yields `undefined`, which would silently look
 * like an omitted field rather than an error. `toJs()` returns a `Map` for a
 * dict, matching the conversion already used in `python/bindings.ts`.
 *
 * Anything that is not a proxy passes through untouched.
 */
export function unwrapDict(value: unknown): unknown {
    const maybeProxy = value as { toJs?: () => unknown };
    if (value === null || typeof value !== 'object' || typeof maybeProxy.toJs !== 'function') {
        return value;
    }
    const converted = maybeProxy.toJs();
    return converted instanceof Map ? Object.fromEntries(converted) : converted;
}

/**
 * Read a field from either the options object or a positional fallback.
 * Keeps the call sites in the service layer short and uniform.
 */
export function optionField<T>(
    options: Record<string, unknown> | undefined,
    name: string,
    fallback: T
): T {
    if (options?.[name] !== undefined) {
        return options[name] as T;
    }
    return fallback;
}

/**
 * Convert and validate a params value arriving from step code.
 *
 * Accepts a JS array, a Pyodide PyProxy wrapping a Python list, or
 * `undefined`/`null`. Returns `undefined` for absent **and empty** input so the
 * `params` key never reaches the wire for an unparameterized call — that keeps
 * every existing request byte-identical to its pre-EHQ-115 form.
 *
 * @throws Error naming the method and the offending index when a value is not a
 * JSON scalar, or is a number SQL cannot receive.
 */
export function toTypedParams(value: unknown, method: string): TypedParam[] | undefined {
    if (value === undefined || value === null) {
        return undefined;
    }

    // Pyodide hands a Python list across as a PyProxy; unwrap it the same way
    // the rest of the Python bridge does.
    const maybeProxy = value as { toJs?: () => unknown };
    const raw = typeof maybeProxy.toJs === 'function' ? maybeProxy.toJs() : value;

    if (!Array.isArray(raw)) {
        throw new TypeError(
            `${method}: params must be an array of values, positional for $1, $2, ... ` +
                `(received ${describe(raw)})`
        );
    }

    if (raw.length === 0) {
        return undefined;
    }

    return raw.map((item, i) => coerce(item, i, method));
}

function coerce(item: unknown, index: number, method: string): TypedParam {
    // $n is 1-based in SQL; report the placeholder the author actually wrote.
    const placeholder = `$${index + 1}`;

    if (item === null || item === undefined) {
        // Both spellings of "no value" bind as SQL NULL. Python None arrives as
        // null once unwrapped; an undefined hole in a JS array arrives as
        // undefined and would otherwise serialize to null silently anyway.
        return null;
    }

    switch (typeof item) {
        case 'string':
        case 'boolean':
            return item;
        case 'number':
            if (!Number.isFinite(item)) {
                // JSON.stringify turns NaN/Infinity into null, which would
                // silently bind SQL NULL instead of failing.
                throw new RangeError(
                    `${method}: params ${placeholder} is ${item}, which cannot be sent as a ` +
                        `SQL parameter`
                );
            }
            return item;
        case 'bigint':
            // newton binds integers as i64, but JSON numbers lose precision past
            // 2^53. Reject rather than quietly corrupt a large id.
            if (
                item > BigInt(Number.MAX_SAFE_INTEGER) ||
                item < BigInt(Number.MIN_SAFE_INTEGER)
            ) {
                throw new Error(
                    `${method}: params ${placeholder} exceeds the exactly-representable integer ` +
                        `range; pass it as a string and cast in SQL`
                );
            }
            return Number(item);
        default:
            throw new Error(
                `${method}: params ${placeholder} must be a string, number, boolean, or null — ` +
                    `got ${describe(item)}. A parameter is a single value; build a list-valued ` +
                    `comparison with one placeholder per element.`
            );
    }
}

function describe(value: unknown): string {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'an array';
    return typeof value;
}

/**
 * The options object, if step code used the options form (EHQ-115).
 *
 * Every query method accepts either its historical positional arguments or a
 * single options object. Returning `undefined` for the positional case lets each
 * method read its fields through `optionField(o, name, args[i])` and support both
 * shapes without branching.
 */
export function optionsOf(args: unknown[]): Record<string, unknown> | undefined {
    // Unwrap first: a Python dict arrives as a PyProxy whose fields are not
    // readable by bracket access, so testing and reading it raw would silently
    // treat every field as omitted.
    const first = unwrapDict(args[0]);
    return isQueryOptions(first) ? first : undefined;
}

/**
 * Resolve the arguments of the two DataFusion query methods.
 *
 * These two are the awkward ones: the historical positional form is already
 * overloaded as `[query]` or `[dataSetName, query]`, so a trailing params slot
 * cannot be resolved by position alone. It is resolved by **type** instead, which
 * is exact rather than heuristic — a dataset name is always a string and a params
 * list is always an array, so the second argument's type decides with no overlap:
 *
 *   [query]                        -> no dataset, no params
 *   [query, params]                -> args[1] is an array
 *   [dataSetName, query]           -> args[1] is a string
 *   [dataSetName, query, params]
 *   [{ query, datasetName?, params? }]
 *
 * Anything else in args[1] is a mistake worth naming rather than guessing at —
 * the dataSetName regression in SCRUM-50 survived for months precisely because a
 * wrong argument was silently ignored instead of reported.
 */
export function resolveQueryArgs(
    args: any[],
    method: string
): { dataSetName: string | undefined; query: string; params: TypedParam[] | undefined } {
    const o = optionsOf(args);
    if (o) {
        const query = o['query'];
        if (typeof query !== 'string') {
            throw new TypeError(`${method}: options must include a 'query' string`);
        }
        // Accept both spellings: the docs and the JS API use datasetName, while
        // the client's own parameter is dataSetName.
        const rawName = o['datasetName'] ?? o['dataSetName'];
        // Validate rather than cast. A non-string name would otherwise reach the
        // client, miss the dataset lookup, and fall back to flight_id 0 — the
        // query would run without the dataset registered and fail with an
        // unrelated "table not found", or worse, silently resolve against a
        // similarly-named static table. Same silent-drop shape as SCRUM-50.
        if (rawName !== undefined && rawName !== null && typeof rawName !== 'string') {
            throw new Error(
                `${method}: datasetName must be a string when provided — got ${describe(rawName)}`
            );
        }
        return {
            dataSetName: (rawName ?? undefined) as string | undefined,
            query,
            params: toTypedParams(o['params'], method),
        };
    }

    if (args.length === 0 || typeof args[0] !== 'string') {
        throw new Error(
            `${method}: first argument must be the SQL string, a dataset name, or an options object`
        );
    }

    if (args.length === 1) {
        return { dataSetName: undefined, query: args[0], params: undefined };
    }

    if (Array.isArray(args[1])) {
        // (query, params)
        return {
            dataSetName: undefined,
            query: args[0],
            params: toTypedParams(args[1], method),
        };
    }

    if (typeof args[1] === 'string') {
        // (dataSetName, query [, params])
        return {
            dataSetName: args[0],
            query: args[1],
            params: toTypedParams(args[2], method),
        };
    }

    throw new Error(
        `${method}: second argument must be the SQL string (when the first is a dataset name) ` +
            `or a params array — got ${describe(args[1])}`
    );
}
